// Third party packages
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { ToastrService } from "ngx-toastr";
import { from, of } from "rxjs";
import { catchError, map, take, tap } from "rxjs/operators";
// Custom packages
import { deadlinesV3ExportErrorKind } from "src/app/modules/deadlines-v3/deadlines-v3.utils";
import { DocumentArchiveDocsService } from "src/app/modules/properties/document-archive-docs.service";
import { DOCUMENT_ARCHIVE_XLSX_TYPE, documentArchiveExportErrorMessage, documentArchiveExportFileName, documentArchiveExportFileNameKey, } from "src/app/modules/properties/document-archive-export.utils";
import { generalSearchRetrySeconds } from "src/app/modules/search-docs/document-archive-general-search.utils";
import * as i0 from "@angular/core";
import * as i1 from "./document-archive-docs.service";
import * as i2 from "@ngx-translate/core";
import * as i3 from "ngx-toastr";
/**
 * «Esporta inseriti» and «Esporta mancanti» from the 3.0.0 archive (NEW-M7) for the legacy pages that
 * still offer them once the switch is live: the «SCARICA» column of the property home, the «Esporta»
 * menu of the mapping and the property list of the statistics. Same Excel, same file name and same
 * messages as the «Esporta» menu of the 3.0.0 dossier (FE-M8).
 *
 * @since 3.0.0
 */
var DocumentArchiveExportDownloadService = /** @class */ (function () {
    /**
     * Class constructor
     */
    function DocumentArchiveExportDownloadService(documentArchiveDocsService, translateService, toastrService) {
        this.documentArchiveDocsService = documentArchiveDocsService;
        this.translateService = translateService;
        this.toastrService = toastrService;
    }
    /**
     * Download one of the two Excels of a property and save it. A failure ends in an error toast, told
     * apart by status as the dossier does.
     *
     * Emits once — `true` when the file was saved, `false` otherwise — then completes: it never errors,
     * so a page only has to know when it is over. Unsubscribing before that drops both the file and the
     * toast.
     *
     * @since 3.0.0
     *
     * @param target     Which Excel
     * @param propertyId The property
     */
    DocumentArchiveExportDownloadService.prototype.download = function (target, propertyId) {
        var _this = this;
        var fileName = documentArchiveExportFileName(this.translateService.instant(documentArchiveExportFileNameKey(target)), new Date());
        return this.documentArchiveDocsService.exportPropertyDocs(target, propertyId).pipe(take(1), map(function (res) {
            // ApiService emits a failure without status (network down) as a VALUE, not as an error
            if (res instanceof Error) {
                _this.toastrService.error(_this.translateService.instant("searchDocs.v3.messages.network"));
                return false;
            }
            var body = res ? res.body : null;
            if (!(body instanceof Blob)) {
                _this.toastrService.error(_this.translateService.instant("properties.documentsV3.export.error"));
                return false;
            }
            _this.saveBlob(new Blob([body], { type: DOCUMENT_ARCHIVE_XLSX_TYPE }), fileName);
            return true;
        }), catchError(function (err) {
            return _this.errorMessage(err).pipe(tap(function (message) { return _this.toastrService.error(message); }), map(function () { return false; }));
        }));
    };
    /**
     * Hand a downloaded Excel to the browser: the one place a file is written, so the tests can replace
     * it.
     *
     * @since 3.0.0
     *
     * @param blob     The Excel
     * @param fileName Its name
     */
    DocumentArchiveExportDownloadService.prototype.saveBlob = function (blob, fileName) {
        FileSaver.saveAs(blob, fileName);
    };
    /**
     * The message of a failed Excel, worded as the dossier words it: the wait after a 429, the role
     * after a 403, the connection without a status, otherwise the message of the back-end, read from
     * the Blob of the answer, or the generic one.
     *
     * @since 3.0.0
     *
     * @param err The error of the download
     */
    DocumentArchiveExportDownloadService.prototype.errorMessage = function (err) {
        var _this = this;
        switch (deadlinesV3ExportErrorKind(err)) {
            case "rateLimited":
                return of(this.translateService.instant("deadlines.v3.messages.exportRateLimited", {
                    seconds: generalSearchRetrySeconds(err),
                }));
            case "forbidden":
                return of(this.translateService.instant("properties.documentsV3.export.forbidden"));
            case "network":
                return of(this.translateService.instant("searchDocs.v3.messages.network"));
            default:
                return from(documentArchiveExportErrorMessage(err)).pipe(map(function (message) {
                    return message ? message : _this.translateService.instant("properties.documentsV3.export.error");
                }));
        }
    };
    DocumentArchiveExportDownloadService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function DocumentArchiveExportDownloadService_Factory() { return new DocumentArchiveExportDownloadService(i0.ɵɵinject(i1.DocumentArchiveDocsService), i0.ɵɵinject(i2.TranslateService), i0.ɵɵinject(i3.ToastrService)); }, token: DocumentArchiveExportDownloadService, providedIn: "root" });
    return DocumentArchiveExportDownloadService;
}());
export { DocumentArchiveExportDownloadService };
